/**
 * ESG Supplier Dashboard Service
 * Comprehensive supplier ecosystem management, performance monitoring, and ESG compliance
 * Following industry standards for supplier relationship management and ESG assessment
 * 
 * Standards Compliance:
 * - ISO 20400: Sustainable Procurement Guidelines
 * - UN Global Compact: Supply Chain Sustainability
 * - CDP Supply Chain Program: Environmental Disclosure
 * - EcoVadis: Supplier Sustainability Ratings
 * - SASB: Materiality Standards for Supply Chain
 * - GRI 308/414: Supplier Environmental and Social Assessment
 * - ILO Conventions: Labor Standards in Supply Chains
 * - OECD Guidelines: Multinational Enterprises Supply Chain Due Diligence
 * - Science Based Targets Initiative (SBTi): Scope 3 Emissions
 * - Fair Trade Standards: Ethical Sourcing Practices
 */

const ESG_SUPPLIER_DASHBOARD_KEY = 'esg-supplier-dashboard-v1';

// Industry-standard seed data for comprehensive supplier dashboard
const esgSupplierDashboardSeed = {
  // Supplier Portfolio Overview
  supplierPortfolio: {
    totalSuppliers: 1847,
    activeSuppliers: 1652,
    preferredSuppliers: 289,
    criticalSuppliers: 156,
    newSuppliers: 94,
    suspendedSuppliers: 23,
    diverseSuppliers: 487,
    certifiedSuppliers: 892,
    totalSpend: 1234567890,
    contractValue: 2847659123,
    avgContractTerm: 3.2,
    supplierRetention: 91.4
  },

  // ESG Performance Metrics
  esgMetrics: {
    overallESGScore: 78.6,
    environmentalScore: 76.2,
    socialScore: 82.1,
    governanceScore: 77.4,
    riskScore: 24.8,
    complianceRate: 94.7,
    certificationRate: 67.3,
    improvementRate: 15.8,
    benchmarkPosition: 'Top 25%',
    industryRanking: 'Above Average'
  },

  // Supplier Performance Data
  suppliers: [
    {
      id: 'SUP-001',
      name: 'Global Manufacturing Solutions Ltd.',
      category: 'Primary Manufacturing',
      tier: 'Tier 1',
      location: 'Vietnam',
      region: 'Asia-Pacific',
      status: 'Active',
      relationship: 'Preferred',
      criticality: 'High',
      spend: 45600000,
      contractValue: 89200000,
      contractExpiry: '2025-12-31',
      riskLevel: 'Medium',
      businessContinuity: 'Secured',
      esgScores: {
        overall: 82.4,
        environmental: 79.8,
        social: 86.2,
        governance: 81.3,
        ethics: 84.7
      },
      certifications: [
        { name: 'ISO 14001', status: 'Valid', expiry: '2025-08-15', score: 94.2 },
        { name: 'SA8000', status: 'Valid', expiry: '2025-11-30', score: 88.6 },
        { name: 'OHSAS 18001', status: 'Valid', expiry: '2025-06-20', score: 91.3 },
        { name: 'ISO 26000', status: 'Valid', expiry: '2025-09-10', score: 87.9 },
        { name: 'Fair Trade', status: 'Valid', expiry: '2025-07-25', score: 92.1 }
      ],
      compliance: {
        laborStandards: { score: 89.4, status: 'Compliant', lastAudit: '2024-07-15' },
        environmentalRegs: { score: 91.2, status: 'Compliant', lastAudit: '2024-08-22' },
        humanRights: { score: 86.7, status: 'Compliant', lastAudit: '2024-06-30' },
        antiCorruption: { score: 93.8, status: 'Compliant', lastAudit: '2024-08-05' },
        productSafety: { score: 88.9, status: 'Compliant', lastAudit: '2024-07-18' }
      },
      performance: {
        qualityScore: 94.3,
        deliveryPerformance: 97.2,
        costCompetitiveness: 85.7,
        innovation: 78.9,
        responsiveness: 91.6,
        sustainability: 83.4
      },
      risks: [
        { type: 'Geopolitical', level: 'Medium', impact: 'Moderate', mitigation: 'Active monitoring' },
        { type: 'Environmental', level: 'Low', impact: 'Low', mitigation: 'Standards compliance' },
        { type: 'Labor', level: 'Low', impact: 'Low', mitigation: 'Regular audits' },
        { type: 'Financial', level: 'Low', impact: 'Low', mitigation: 'Credit monitoring' }
      ],
      sustainabilityInitiatives: [
        { name: 'Carbon Neutrality Program', progress: 67.8, target: '2026', investment: 2400000 },
        { name: 'Water Efficiency Project', progress: 84.2, target: '2025', investment: 890000 },
        { name: 'Waste Reduction Initiative', progress: 91.5, target: '2024', investment: 560000 },
        { name: 'Worker Training Program', progress: 78.3, target: '2025', investment: 320000 }
      ],
      keyMetrics: {
        carbonFootprint: { value: 2847, unit: 'tCO2e', trend: '-12.4%', target: 2200 },
        waterUsage: { value: 45600, unit: 'm³', trend: '-8.7%', target: 38000 },
        wasteGeneration: { value: 567, unit: 'tonnes', trend: '-15.3%', target: 420 },
        energyConsumption: { value: 8900, unit: 'MWh', trend: '-6.8%', target: 7800 },
        employeeSatisfaction: { value: 87.4, unit: '%', trend: '+4.2%', target: 90.0 },
        localEmployment: { value: 78.9, unit: '%', trend: '+2.1%', target: 80.0 }
      },
      contactInfo: {
        primaryContact: 'James Chen',
        email: 'j.chen@gms-ltd.com',
        phone: '+84-28-1234-5678',
        esgContact: 'Maria Nguyen',
        esgEmail: 'm.nguyen@gms-ltd.com'
      },
      lastAssessment: '2024-08-15T09:30:00Z',
      nextReview: '2024-11-15T14:00:00Z'
    },
    {
      id: 'SUP-002',
      name: 'European Logistics Partners AG',
      category: 'Logistics & Transportation',
      tier: 'Tier 1',
      location: 'Germany',
      region: 'Europe',
      status: 'Active',
      relationship: 'Strategic',
      criticality: 'Critical',
      spend: 28900000,
      contractValue: 67400000,
      contractExpiry: '2026-06-30',
      riskLevel: 'Low',
      businessContinuity: 'Secured',
      esgScores: {
        overall: 88.7,
        environmental: 92.1,
        social: 87.3,
        governance: 86.8,
        ethics: 89.6
      },
      certifications: [
        { name: 'ISO 14001', status: 'Valid', expiry: '2025-12-15', score: 96.8 },
        { name: 'ISO 45001', status: 'Valid', expiry: '2025-10-30', score: 94.2 },
        { name: 'EcoVadis Gold', status: 'Valid', expiry: '2025-08-20', score: 91.7 },
        { name: 'Carbon Trust Standard', status: 'Valid', expiry: '2025-11-10', score: 93.4 }
      ],
      compliance: {
        laborStandards: { score: 95.6, status: 'Excellent', lastAudit: '2024-06-10' },
        environmentalRegs: { score: 97.3, status: 'Excellent', lastAudit: '2024-07-25' },
        humanRights: { score: 91.8, status: 'Compliant', lastAudit: '2024-05-15' },
        antiCorruption: { score: 96.2, status: 'Excellent', lastAudit: '2024-08-12' },
        productSafety: { score: 92.4, status: 'Compliant', lastAudit: '2024-06-28' }
      },
      performance: {
        qualityScore: 96.8,
        deliveryPerformance: 98.7,
        costCompetitiveness: 82.3,
        innovation: 89.4,
        responsiveness: 95.2,
        sustainability: 91.6
      },
      risks: [
        { type: 'Regulatory', level: 'Low', impact: 'Low', mitigation: 'Proactive compliance' },
        { type: 'Technology', level: 'Medium', impact: 'Low', mitigation: 'Innovation partnership' }
      ],
      sustainabilityInitiatives: [
        { name: 'Electric Fleet Transition', progress: 56.8, target: '2027', investment: 8900000 },
        { name: 'Green Logistics Network', progress: 72.4, target: '2026', investment: 3400000 },
        { name: 'Carbon Offset Program', progress: 89.1, target: '2025', investment: 1200000 }
      ],
      keyMetrics: {
        carbonFootprint: { value: 8942, unit: 'tCO2e', trend: '-18.6%', target: 6500 },
        fuelEfficiency: { value: 23.4, unit: 'L/100km', trend: '-9.2%', target: 20.0 },
        recyclingRate: { value: 87.6, unit: '%', trend: '+5.4%', target: 90.0 },
        employeeTraining: { value: 96.3, unit: '% completed', trend: '+3.7%', target: 98.0 },
        safetyIncidents: { value: 0.2, unit: 'per 100k km', trend: '-67.8%', target: 0.1 },
        customerSatisfaction: { value: 94.8, unit: '%', trend: '+2.3%', target: 95.0 }
      },
      contactInfo: {
        primaryContact: 'Klaus Mueller',
        email: 'k.mueller@elp-ag.de',
        phone: '+49-30-1234-5678',
        esgContact: 'Anna Schmidt',
        esgEmail: 'a.schmidt@elp-ag.de'
      },
      lastAssessment: '2024-07-20T11:15:00Z',
      nextReview: '2024-10-20T10:30:00Z'
    },
    {
      id: 'SUP-003',
      name: 'Sustainable Materials Inc.',
      category: 'Raw Materials',
      tier: 'Tier 2',
      location: 'Canada',
      region: 'North America',
      status: 'Active',
      relationship: 'Preferred',
      criticality: 'Medium',
      spend: 12300000,
      contractValue: 34500000,
      contractExpiry: '2025-09-30',
      riskLevel: 'Low',
      businessContinuity: 'Secured',
      esgScores: {
        overall: 91.2,
        environmental: 95.8,
        social: 88.7,
        governance: 89.1,
        ethics: 92.4
      },
      certifications: [
        { name: 'FSC Chain of Custody', status: 'Valid', expiry: '2025-12-31', score: 97.1 },
        { name: 'PEFC Certification', status: 'Valid', expiry: '2025-11-15', score: 94.6 },
        { name: 'B Corp Certified', status: 'Valid', expiry: '2026-03-20', score: 93.8 },
        { name: 'Cradle to Cradle', status: 'Valid', expiry: '2025-10-10', score: 91.2 }
      ],
      compliance: {
        laborStandards: { score: 92.7, status: 'Compliant', lastAudit: '2024-08-05' },
        environmentalRegs: { score: 98.4, status: 'Excellent', lastAudit: '2024-07-12' },
        humanRights: { score: 89.6, status: 'Compliant', lastAudit: '2024-06-18' },
        antiCorruption: { score: 94.3, status: 'Compliant', lastAudit: '2024-08-28' },
        productSafety: { score: 95.7, status: 'Excellent', lastAudit: '2024-07-03' }
      },
      performance: {
        qualityScore: 93.4,
        deliveryPerformance: 95.8,
        costCompetitiveness: 78.2,
        innovation: 92.6,
        responsiveness: 89.7,
        sustainability: 96.3
      },
      risks: [
        { type: 'Climate', level: 'Medium', impact: 'Medium', mitigation: 'Adaptation planning' },
        { type: 'Supply Chain', level: 'Low', impact: 'Low', mitigation: 'Diversified sourcing' }
      ],
      sustainabilityInitiatives: [
        { name: 'Regenerative Forestry', progress: 78.9, target: '2026', investment: 4200000 },
        { name: 'Biodiversity Conservation', progress: 83.4, target: '2025', investment: 2800000 },
        { name: 'Community Development', progress: 65.7, target: '2027', investment: 1900000 }
      ],
      keyMetrics: {
        forestArea: { value: 12500, unit: 'hectares managed', trend: '+3.2%', target: 13000 },
        biodiversityIndex: { value: 0.87, unit: 'index score', trend: '+8.4%', target: 0.90 },
        communityJobs: { value: 234, unit: 'local jobs created', trend: '+12.6%', target: 280 },
        soilHealth: { value: 4.2, unit: 'index score', trend: '+6.8%', target: 4.5 },
        carbonSequestration: { value: 8900, unit: 'tCO2/year', trend: '+15.3%', target: 10000 },
        indigenousPartnership: { value: 89.4, unit: '% protocols followed', trend: '+4.7%', target: 95.0 }
      },
      contactInfo: {
        primaryContact: 'Sarah Thompson',
        email: 's.thompson@smi-canada.com',
        phone: '+1-604-555-0123',
        esgContact: 'David First Nation',
        esgEmail: 'd.firstnation@smi-canada.com'
      },
      lastAssessment: '2024-08-10T13:45:00Z',
      nextReview: '2024-11-10T09:00:00Z'
    }
  ],

  // Risk Assessment Matrix
  riskMatrix: {
    categories: [
      {
        name: 'Environmental Risks',
        subcategories: ['Climate Change Impact', 'Resource Scarcity', 'Pollution & Waste', 'Biodiversity Loss'],
        overallRisk: 'Medium',
        trend: 'Improving',
        suppliersAffected: 342,
        mitigationProgress: 78.6
      },
      {
        name: 'Social Risks',
        subcategories: ['Labor Standards', 'Human Rights', 'Community Relations', 'Health & Safety'],
        overallRisk: 'Low',
        trend: 'Stable',
        suppliersAffected: 156,
        mitigationProgress: 89.4
      },
      {
        name: 'Governance Risks',
        subcategories: ['Business Ethics', 'Anti-Corruption', 'Transparency', 'Legal Compliance'],
        overallRisk: 'Low',
        trend: 'Improving',
        suppliersAffected: 89,
        mitigationProgress: 92.7
      },
      {
        name: 'Operational Risks',
        subcategories: ['Supply Disruption', 'Quality Issues', 'Cyber Security', 'Financial Stability'],
        overallRisk: 'Medium',
        trend: 'Monitoring',
        suppliersAffected: 267,
        mitigationProgress: 84.3
      }
    ],
    heatMap: [
      { category: 'Climate Physical Risk', likelihood: 'High', impact: 'Medium', suppliers: 89, riskScore: 7.2 },
      { category: 'Labor Standards Risk', likelihood: 'Low', impact: 'High', suppliers: 23, riskScore: 4.8 },
      { category: 'Corruption Risk', likelihood: 'Medium', impact: 'High', suppliers: 67, riskScore: 6.4 },
      { category: 'Supply Disruption Risk', likelihood: 'Medium', impact: 'Medium', suppliers: 134, riskScore: 5.6 },
      { category: 'Cyber Security Risk', likelihood: 'High', impact: 'Low', suppliers: 178, riskScore: 3.9 }
    ]
  },

  // Performance Analytics
  performanceAnalytics: {
    trends: {
      esgScoreImprovement: '+6.8%',
      complianceRate: '+2.4%',
      riskReduction: '-12.3%',
      certificationIncrease: '+18.7%',
      sustainabilityInvestment: '+34.2%'
    },
    benchmarks: {
      industryAverage: 74.2,
      topQuartile: 86.5,
      ourPosition: 78.6,
      targetScore: 85.0,
      improvementRequired: 6.4
    },
    categoryBreakdown: {
      environmental: { current: 76.2, target: 82.0, gap: -5.8, trend: '+4.1%' },
      social: { current: 82.1, target: 85.0, gap: -2.9, trend: '+3.2%' },
      governance: { current: 77.4, target: 83.0, gap: -5.6, trend: '+2.8%' }
    }
  },

  // Certification Management
  certificationTracking: {
    totalCertifications: 1247,
    validCertifications: 1156,
    expiringIn30Days: 23,
    expiringIn90Days: 67,
    expiredCertifications: 91,
    renewalSuccess: 94.3,
    averageScore: 91.7,
    complianceGaps: 8,
    certificationTypes: [
      { name: 'ISO 14001', count: 289, valid: 267, avgScore: 92.4 },
      { name: 'SA8000', count: 156, valid: 148, avgScore: 89.7 },
      { name: 'OHSAS 18001', count: 134, valid: 129, avgScore: 91.2 },
      { name: 'Fair Trade', count: 89, valid: 84, avgScore: 94.6 },
      { name: 'FSC', count: 67, valid: 62, avgScore: 93.8 },
      { name: 'EcoVadis', count: 178, valid: 169, avgScore: 88.9 }
    ]
  },

  // Diversity & Inclusion Metrics
  diversityMetrics: {
    diverseSuppliers: 487,
    diversitySpend: 234567890,
    diversityPercentage: 32.4,
    categories: {
      womenOwned: { count: 156, spend: 89234567, percentage: 12.8 },
      minorityOwned: { count: 134, spend: 67890123, percentage: 9.1 },
      smallBusiness: { count: 198, spend: 78456789, percentage: 10.5 },
      veteranOwned: { count: 45, spend: 23456789, percentage: 3.2 },
      lgbtOwned: { count: 34, spend: 18901234, percentage: 2.4 },
      disabilityOwned: { count: 28, spend: 15678901, percentage: 1.9 }
    },
    targets: {
      overall: { target: 40.0, current: 32.4, progress: 81.0 },
      womenOwned: { target: 18.0, current: 12.8, progress: 71.1 },
      minorityOwned: { target: 15.0, current: 9.1, progress: 60.7 }
    }
  },

  // Sustainability Initiatives
  sustainabilityPrograms: [
    {
      name: 'Carbon Reduction Partnership',
      description: 'Collaborative program to reduce Scope 3 emissions across supply chain',
      participants: 234,
      totalInvestment: 12400000,
      progressScore: 73.8,
      metrics: {
        carbonReduced: { value: 23450, unit: 'tCO2e', target: 35000 },
        energySaved: { value: 8900, unit: 'MWh', target: 12000 },
        renewableAdoption: { value: 67.4, unit: '%', target: 80.0 }
      },
      milestones: [
        { name: 'Baseline Assessment', status: 'Complete', date: '2024-03-15' },
        { name: 'Target Setting', status: 'Complete', date: '2024-05-30' },
        { name: 'Implementation Phase 1', status: 'In Progress', date: '2024-12-31' },
        { name: 'Mid-term Review', status: 'Scheduled', date: '2025-06-30' }
      ]
    },
    {
      name: 'Ethical Sourcing Excellence',
      description: 'Program to ensure highest ethical standards across procurement',
      participants: 189,
      totalInvestment: 5600000,
      progressScore: 86.2,
      metrics: {
        auditsPassed: { value: 94.7, unit: '%', target: 98.0 },
        violationsResolved: { value: 89.3, unit: '%', target: 95.0 },
        trainingCompleted: { value: 91.8, unit: '%', target: 98.0 }
      },
      milestones: [
        { name: 'Standards Development', status: 'Complete', date: '2024-02-28' },
        { name: 'Supplier Training', status: 'Complete', date: '2024-07-15' },
        { name: 'Audit Program', status: 'In Progress', date: '2024-11-30' },
        { name: 'Certification Phase', status: 'Planned', date: '2025-03-31' }
      ]
    },
    {
      name: 'Circular Economy Initiative',
      description: 'Promoting circular economy principles and waste reduction',
      participants: 145,
      totalInvestment: 8900000,
      progressScore: 62.4,
      metrics: {
        wasteReduced: { value: 45.6, unit: '%', target: 60.0 },
        materialsRecycled: { value: 78.9, unit: '%', target: 85.0 },
        circularProducts: { value: 34.2, unit: '% of portfolio', target: 50.0 }
      },
      milestones: [
        { name: 'Pilot Projects', status: 'Complete', date: '2024-04-30' },
        { name: 'Technology Deployment', status: 'In Progress', date: '2024-10-31' },
        { name: 'Scale-up Phase', status: 'Planned', date: '2025-08-31' }
      ]
    }
  ]
};

// Service implementation
let supplierDashboardData = null;
let subscribers = [];

const initializeData = () => {
  if (!supplierDashboardData) {
    const stored = localStorage.getItem(ESG_SUPPLIER_DASHBOARD_KEY);
    supplierDashboardData = stored ? JSON.parse(stored) : esgSupplierDashboardSeed;
    persistData();
  }
  return supplierDashboardData;
};

const persistData = () => {
  if (supplierDashboardData) {
    localStorage.setItem(ESG_SUPPLIER_DASHBOARD_KEY, JSON.stringify(supplierDashboardData));
    notifySubscribers();
  }
};

const notifySubscribers = () => {
  subscribers.forEach(callback => callback(supplierDashboardData));
};

// Public API
export const subscribe = (callback) => {
  subscribers.push(callback);
  callback(initializeData());
  
  return () => {
    subscribers = subscribers.filter(sub => sub !== callback);
  };
};

export const getSupplierDashboardOverview = () => {
  const data = initializeData();
  return {
    ...data.supplierPortfolio,
    ...data.esgMetrics,
    riskSummary: {
      totalRisks: data.riskMatrix.heatMap.length,
      highRisks: data.riskMatrix.heatMap.filter(r => r.riskScore >= 7.0).length,
      mediumRisks: data.riskMatrix.heatMap.filter(r => r.riskScore >= 4.0 && r.riskScore < 7.0).length,
      lowRisks: data.riskMatrix.heatMap.filter(r => r.riskScore < 4.0).length
    },
    certificationSummary: data.certificationTracking,
    diversitySummary: data.diversityMetrics,
    programsSummary: {
      totalPrograms: data.sustainabilityPrograms.length,
      totalParticipants: data.sustainabilityPrograms.reduce((sum, p) => sum + p.participants, 0),
      totalInvestment: data.sustainabilityPrograms.reduce((sum, p) => sum + p.totalInvestment, 0),
      averageProgress: data.sustainabilityPrograms.reduce((sum, p) => sum + p.progressScore, 0) / data.sustainabilityPrograms.length
    }
  };
};

export const getSuppliers = () => initializeData().suppliers;
export const getSupplierPortfolio = () => initializeData().supplierPortfolio;
export const getESGMetrics = () => initializeData().esgMetrics;
export const getRiskMatrix = () => initializeData().riskMatrix;
export const getPerformanceAnalytics = () => initializeData().performanceAnalytics;
export const getCertificationTracking = () => initializeData().certificationTracking;
export const getDiversityMetrics = () => initializeData().diversityMetrics;
export const getSustainabilityPrograms = () => initializeData().sustainabilityPrograms;

export const getSupplierById = (id) => {
  const data = initializeData();
  return data.suppliers.find(supplier => supplier.id === id);
};

export const getSuppliersByCategory = (category) => {
  const data = initializeData();
  return data.suppliers.filter(supplier => supplier.category === category);
};

export const getSuppliersByRiskLevel = (riskLevel) => {
  const data = initializeData();
  return data.suppliers.filter(supplier => supplier.riskLevel === riskLevel);
};

export const getSuppliersByTier = (tier) => {
  const data = initializeData();
  return data.suppliers.filter(supplier => supplier.tier === tier);
};

export const getSuppliersByRegion = (region) => {
  const data = initializeData();
  return data.suppliers.filter(supplier => supplier.region === region);
};

export const getHighPerformingSuppliers = () => {
  const data = initializeData();
  return data.suppliers
    .filter(supplier => supplier.esgScores.overall >= 85.0)
    .sort((a, b) => b.esgScores.overall - a.esgScores.overall);
};

export const getRiskSuppliers = () => {
  const data = initializeData();
  return data.suppliers
    .filter(supplier => supplier.riskLevel === 'High' || supplier.esgScores.overall < 70.0)
    .sort((a, b) => a.esgScores.overall - b.esgScores.overall);
};

export const getCertificationExpiringSuppliers = (days = 90) => {
  const data = initializeData();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() + days);
  
  return data.suppliers.filter(supplier => 
    supplier.certifications.some(cert => {
      const expiryDate = new Date(cert.expiry);
      return expiryDate <= cutoffDate && cert.status === 'Valid';
    })
  );
};

export const getSupplierPerformanceTrends = () => {
  return {
    esgTrend: '+6.8%',
    complianceTrend: '+2.4%',
    performanceTrend: '+4.1%',
    riskTrend: '-12.3%',
    certificationTrend: '+18.7%',
    sustainabilityTrend: '+34.2%'
  };
};

export const getSupplierSpendAnalysis = () => {
  const data = initializeData();
  const totalSpend = data.supplierPortfolio.totalSpend;
  
  return {
    totalSpend,
    spendByTier: {
      tier1: { amount: totalSpend * 0.65, percentage: 65.0 },
      tier2: { amount: totalSpend * 0.25, percentage: 25.0 },
      tier3: { amount: totalSpend * 0.10, percentage: 10.0 }
    },
    spendByRisk: {
      low: { amount: totalSpend * 0.72, percentage: 72.0 },
      medium: { amount: totalSpend * 0.23, percentage: 23.0 },
      high: { amount: totalSpend * 0.05, percentage: 5.0 }
    },
    spendByRegion: {
      'Asia-Pacific': { amount: totalSpend * 0.42, percentage: 42.0 },
      'Europe': { amount: totalSpend * 0.28, percentage: 28.0 },
      'North America': { amount: totalSpend * 0.20, percentage: 20.0 },
      'Latin America': { amount: totalSpend * 0.06, percentage: 6.0 },
      'Africa': { amount: totalSpend * 0.04, percentage: 4.0 }
    }
  };
};

export const updateSupplierESGScore = (supplierId, newScores) => {
  const data = initializeData();
  const supplier = data.suppliers.find(s => s.id === supplierId);
  
  if (supplier) {
    supplier.esgScores = { ...supplier.esgScores, ...newScores };
    supplier.lastAssessment = new Date().toISOString();
    persistData();
  }
};

export const addSupplierCertification = (supplierId, certification) => {
  const data = initializeData();
  const supplier = data.suppliers.find(s => s.id === supplierId);
  
  if (supplier) {
    supplier.certifications.push(certification);
    persistData();
  }
};

export const updateSupplierRiskLevel = (supplierId, newRiskLevel) => {
  const data = initializeData();
  const supplier = data.suppliers.find(s => s.id === supplierId);
  
  if (supplier) {
    supplier.riskLevel = newRiskLevel;
    persistData();
  }
};

export const addSustainabilityInitiative = (supplierId, initiative) => {
  const data = initializeData();
  const supplier = data.suppliers.find(s => s.id === supplierId);
  
  if (supplier) {
    supplier.sustainabilityInitiatives.push(initiative);
    persistData();
  }
};

export const generateSupplierReport = (supplierId = null, reportType = 'comprehensive') => {
  const data = initializeData();
  
  let reportData = {
    overview: getSupplierDashboardOverview(),
    performanceAnalytics: data.performanceAnalytics,
    riskMatrix: data.riskMatrix,
    certificationTracking: data.certificationTracking,
    diversityMetrics: data.diversityMetrics,
    sustainabilityPrograms: data.sustainabilityPrograms,
    generatedAt: new Date().toISOString(),
    reportType
  };

  if (supplierId) {
    reportData.supplier = getSupplierById(supplierId);
  } else {
    reportData.suppliers = data.suppliers;
  }

  return reportData;
};

export const exportSupplierData = (format = 'json', filterCriteria = {}) => {
  const data = initializeData();
  
  let filteredData = { ...data };
  
  if (filterCriteria.category) {
    filteredData.suppliers = data.suppliers.filter(s => s.category === filterCriteria.category);
  }
  
  if (filterCriteria.riskLevel) {
    filteredData.suppliers = (filteredData.suppliers || data.suppliers)
      .filter(s => s.riskLevel === filterCriteria.riskLevel);
  }
  
  if (filterCriteria.tier) {
    filteredData.suppliers = (filteredData.suppliers || data.suppliers)
      .filter(s => s.tier === filterCriteria.tier);
  }
  
  if (filterCriteria.region) {
    filteredData.suppliers = (filteredData.suppliers || data.suppliers)
      .filter(s => s.region === filterCriteria.region);
  }

  return {
    ...filteredData,
    exportedAt: new Date().toISOString(),
    filterCriteria
  };
};

export const refreshSupplierData = async () => {
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // In a real application, this would fetch fresh data from the server
  const data = initializeData();
  
  // Update last refresh timestamp
  data.lastRefresh = new Date().toISOString();
  
  // Simulate some data updates
  data.esgMetrics.overallESGScore = Math.min(100, data.esgMetrics.overallESGScore + Math.random() * 2 - 1);
  data.esgMetrics.complianceRate = Math.min(100, data.esgMetrics.complianceRate + Math.random() * 1 - 0.5);
  data.supplierPortfolio.activeSuppliers = data.supplierPortfolio.totalSuppliers - Math.floor(Math.random() * 10);
  
  persistData();
  return data;
};